import type { CenterKey } from '@/navigation/centerNavigation';
import type { AuthorizedResourceAccessMap } from './adapter';
import { PERMISSION_RESOURCE_CODES } from './definitions';

export type EmployeePortalEffectiveFeatures = {
  bound: boolean;
  features: Record<string, boolean>;
};

const PROFILE_FEATURE = 'feature.profile';
const CERTIFICATE_HISTORY_FEATURE = 'feature.certificate_history';
const CERTIFICATE_REQUEST_FEATURE = 'feature.certificate_request';
const RESIGNATION_HANDOVER_FEATURE = 'feature.resignation_handover';
const ATTENDANCE_FEATURE = 'feature.attendance';
const LEAVE_BALANCE_FEATURE = 'feature.leave_balance';
const LEAVE_REQUEST_FEATURE = 'feature.leave_request';
const OVERTIME_REQUEST_FEATURE = 'feature.overtime_request';
const PUNCH_REQUEST_FEATURE = 'feature.punch_request';
const APPROVAL_FEATURE = 'feature.approval';
const PAYROLL_FEATURE = 'feature.payroll';
const SOCIAL_INSURANCE_FEATURE = 'feature.social_insurance';
const PERFORMANCE_FEATURE = 'feature.performance';

const setResourceAccess = (
  access: AuthorizedResourceAccessMap,
  resourceCode: string,
  enabled: boolean,
) => {
  if (enabled) return;
  Object.keys(access).forEach((key) => {
    if (key === resourceCode || key.startsWith(`${resourceCode}:`)) {
      access[key] = false;
    }
  });
  access[resourceCode] = false;
};

const setActionAccess = (
  access: AuthorizedResourceAccessMap,
  resourceCode: string,
  actions: string[],
  enabled: boolean,
) => {
  if (enabled) return;
  actions.forEach((action) => {
    access[`${resourceCode}:${action}`] = false;
  });
};

export const getEffectiveEmployeePortalAccess = (
  authorizedResourceAccess: AuthorizedResourceAccessMap,
  effectiveFeatures?: EmployeePortalEffectiveFeatures,
): AuthorizedResourceAccessMap => {
  const nextAccess = { ...authorizedResourceAccess };
  const isVisible = (featureKey: string) =>
    effectiveFeatures?.bound === true &&
    effectiveFeatures.features?.[featureKey] === true;
  const profileVisible = isVisible(PROFILE_FEATURE);
  const certificateHistoryVisible = isVisible(CERTIFICATE_HISTORY_FEATURE);
  const certificateRequestVisible = isVisible(CERTIFICATE_REQUEST_FEATURE);
  const resignationHandoverVisible = isVisible(RESIGNATION_HANDOVER_FEATURE);
  const attendanceVisible = isVisible(ATTENDANCE_FEATURE);
  const leaveBalanceVisible = isVisible(LEAVE_BALANCE_FEATURE);
  const leaveRequestVisible = isVisible(LEAVE_REQUEST_FEATURE);
  const overtimeRequestVisible = isVisible(OVERTIME_REQUEST_FEATURE);
  const punchRequestVisible = isVisible(PUNCH_REQUEST_FEATURE);
  const approvalVisible = isVisible(APPROVAL_FEATURE);
  const payrollVisible = isVisible(PAYROLL_FEATURE);
  const socialInsuranceVisible = isVisible(SOCIAL_INSURANCE_FEATURE);
  const performanceVisible = isVisible(PERFORMANCE_FEATURE);

  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.selfProfile,
    profileVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.selfLifecycle,
    profileVisible,
  );

  const certificateResource = PERMISSION_RESOURCE_CODES.selfCertificateRequests;
  setResourceAccess(
    nextAccess,
    certificateResource,
    certificateHistoryVisible || certificateRequestVisible,
  );
  setActionAccess(
    nextAccess,
    certificateResource,
    ['query', 'download-result'],
    certificateHistoryVisible,
  );
  setActionAccess(
    nextAccess,
    certificateResource,
    [
      'create-request',
      'edit-request',
      'submit-request',
      'cancel-request',
      'withdraw-request',
    ],
    certificateRequestVisible,
  );
  // Temporarily unavailable in the employee portal; retain the implementation.
  setResourceAccess(nextAccess, certificateResource, false);

  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.selfResignationHandover,
    resignationHandoverVisible,
  );

  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyOverview,
    attendanceVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyRecords,
    attendanceVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyMonthly,
    attendanceVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyLeaveBalances,
    attendanceVisible && leaveBalanceVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    attendanceVisible,
  );
  setActionAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    ['create-leave'],
    attendanceVisible && leaveRequestVisible,
  );
  setActionAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    ['create-overtime'],
    attendanceVisible && overtimeRequestVisible,
  );
  setActionAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    ['create-punch'],
    attendanceVisible && punchRequestVisible,
  );
  setActionAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    ['submit-request'],
    attendanceVisible &&
      (leaveRequestVisible || overtimeRequestVisible || punchRequestVisible),
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.attendanceApproval,
    attendanceVisible && approvalVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.payrollMy,
    payrollVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.socialInsuranceMy,
    socialInsuranceVisible,
  );
  setResourceAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.performanceMy,
    performanceVisible,
  );
  setActionAccess(
    nextAccess,
    PERMISSION_RESOURCE_CODES.performanceMy,
    ['confirm-interview'],
    false,
  );

  return nextAccess;
};

export const PERSONAL_ROUTES = [
  { path: '/me/profile', resource: PERMISSION_RESOURCE_CODES.selfProfile },
  { path: '/me/lifecycle', resource: PERMISSION_RESOURCE_CODES.selfLifecycle },
  {
    path: '/me/resignation-handover',
    resource: PERMISSION_RESOURCE_CODES.selfResignationHandover,
  },
] as const;

export const getFirstPersonalPath = (access: AuthorizedResourceAccessMap) =>
  PERSONAL_ROUTES.find(({ resource }) => access[resource])?.path;

export const ATTENDANCE_ROUTES = [
  {
    path: '/attendance-center/my',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyOverview,
  },
  {
    path: '/attendance-center/my/approval',
    resource: PERMISSION_RESOURCE_CODES.attendanceApproval,
  },
  {
    path: '/attendance-center/my/requests',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
  },
  {
    path: '/attendance-center/my/requests/leave/create',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    action: 'create-leave',
  },
  {
    path: '/attendance-center/my/requests/leave/:uuid',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
  },
  {
    path: '/attendance-center/my/requests/leave/:uuid/edit',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    action: 'create-leave',
  },
  {
    path: '/attendance-center/my/requests/overtime/create',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    action: 'create-overtime',
  },
  {
    path: '/attendance-center/my/requests/overtime/:uuid',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
  },
  {
    path: '/attendance-center/my/requests/overtime/:uuid/edit',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    action: 'create-overtime',
  },
  {
    path: '/attendance-center/my/requests/punch/create',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    action: 'create-punch',
  },
  {
    path: '/attendance-center/my/requests/punch/:uuid',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
  },
  {
    path: '/attendance-center/my/requests/punch/:uuid/edit',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    action: 'create-punch',
  },
  {
    path: '/attendance-center/my/records',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyRecords,
  },
  {
    path: '/attendance-center/my/leave-balances',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyLeaveBalances,
  },
  {
    path: '/attendance-center/my/monthly',
    resource: PERMISSION_RESOURCE_CODES.attendanceMyMonthly,
  },
  {
    path: '/attendance-center/my/approval/leave/:uuid',
    resource: PERMISSION_RESOURCE_CODES.attendanceApproval,
    action: 'open-source',
  },
  {
    path: '/attendance-center/my/approval/overtime/:uuid',
    resource: PERMISSION_RESOURCE_CODES.attendanceApproval,
    action: 'open-source',
  },
  {
    path: '/attendance-center/my/approval/punch/:uuid',
    resource: PERMISSION_RESOURCE_CODES.attendanceApproval,
    action: 'open-source',
  },
] as const;

export const getFirstAttendancePath = (access: AuthorizedResourceAccessMap) =>
  ATTENDANCE_ROUTES.find(({ resource }) => access[resource])?.path;

export const PAYROLL_ROUTES = [
  {
    path: '/payroll-center/my/slips',
    resource: PERMISSION_RESOURCE_CODES.payrollMy,
  },
  {
    path: '/payroll-center/my/slips/:uuid',
    resource: PERMISSION_RESOURCE_CODES.payrollMy,
    action: 'view-slip',
  },
] as const;

export const SOCIAL_INSURANCE_ROUTES = [
  {
    path: '/social-insurance-center/my',
    resource: PERMISSION_RESOURCE_CODES.socialInsuranceMy,
  },
] as const;

export const PERFORMANCE_ROUTES = [
  {
    path: '/performance-center/my',
    resource: PERMISSION_RESOURCE_CODES.performanceMy,
  },
  {
    path: '/performance-center/my/goals/:uuid',
    resource: PERMISSION_RESOURCE_CODES.performanceMy,
  },
  {
    path: '/performance-center/my/reviews/:uuid',
    resource: PERMISSION_RESOURCE_CODES.performanceMy,
  },
  {
    path: '/performance-center/my/interviews/:uuid',
    resource: PERMISSION_RESOURCE_CODES.performanceMy,
  },
  {
    path: '/performance-center/my/results/:uuid',
    resource: PERMISSION_RESOURCE_CODES.performanceMy,
  },
] as const;

export const getFirstPayrollPath = (access: AuthorizedResourceAccessMap) =>
  PAYROLL_ROUTES.find(({ resource }) => access[resource])?.path;

export const getFirstSocialInsurancePath = (
  access: AuthorizedResourceAccessMap,
) => SOCIAL_INSURANCE_ROUTES.find(({ resource }) => access[resource])?.path;

export const getFirstPerformancePath = (access: AuthorizedResourceAccessMap) =>
  PERFORMANCE_ROUTES.find(({ resource }) => access[resource])?.path;

export const getFirstPortalPath = (
  access: AuthorizedResourceAccessMap,
  preferred: CenterKey = 'employee',
) => {
  const centers = {
    employee: getFirstPersonalPath,
    attendance: getFirstAttendancePath,
    payroll: getFirstPayrollPath,
    'social-insurance': getFirstSocialInsurancePath,
    performance: getFirstPerformancePath,
  };
  return (
    centers[preferred](access) ||
    Object.values(centers)
      .map((firstPath) => firstPath(access))
      .find(Boolean)
  );
};
